// @flow

import * as React from "react";
import {NavLink, withRouter} from "react-router-dom";
import {ToastContainer, toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from "axios";
import {showGenericErrorToast, loginErrorFromLocation} from "./Utils";
import logo from "./images/logo.svg"
import {HomeIcon, GridIcon, SettingsIcon, UsersIcon, PlayIcon, DollarSignIcon, HelpCircleIcon, ChevronDownIcon, LogOutIcon, PlusCircleIcon} from "./components/Icons.react";
import AppFooter from "./components/AppFooter.react";
import LegacyPlanModal from "./components/LegacyPlanModal.react";

type Props = {|
  +children: React.Node,
|};

const navItems = [
  {value: "Dashboard", to: "/dashboard", icon: HomeIcon},
  // alsoActiveOn: sub-pages that should keep this item highlighted (New QR Invoice lives under All QR Invoices).
  {value: "All QR Invoices", to: "/all-invoices", icon: GridIcon, alsoActiveOn: ["/new-qr"]},
];

const adminNavItems = [
  {value: "Usage & Plan", to: "/subscriptions", icon: DollarSignIcon},
  {value: "Team", to: "/team", icon: UsersIcon},
];

// Always the last item, after the admin-only ones.
const settingsNavItem = {value: "Settings", to: "/settings", icon: SettingsIcon};

// QuickBooks refused our stored token (the server marked this company disconnected): say so once, without
// blocking the UI. Deliberately no page reload - every page that calls QuickBooks would fail again and reload
// in a loop. Registered once at import time, i.e. before any child page fires its first request (children
// mount before this component's componentDidMount runs). The fixed toastId keeps repeated failures to one toast.
axios.interceptors.response.use((response) => response, (error) => {
    if (error.response && error.response.status === 401 && error.response.data && error.response.data.error === "quickbooks_reconnect_required") {
        toast.error(<span>QuickBooks is not connected for this company. <a href="/oauth/init">Reconnect QuickBooks</a></span>,
                    {toastId: "quickbooks-reconnect-required"});
    }
    return Promise.reject(error);
});

class SiteWrapper extends React.Component<Props, State> {
    state = {
        userSettings: null,
        companies: [],
        currentYear: new Date().getFullYear(),
        sidebarCollapsed: localStorage.getItem("sidebarCollapsed") === "true" || window.innerWidth <= 640,
        showAccountMenu: false,
        isSwitchingCompany: false,
        showLegacyPlanModal: false,
    };

    accountMenuRef = React.createRef();

    toggleAccountMenu = () => {
        this.setState((prevState) => ({showAccountMenu: !prevState.showAccountMenu}));
    };

    handleOutsideClick = (event) => {
        if (this.accountMenuRef.current && !this.accountMenuRef.current.contains(event.target)) {
            this.setState({showAccountMenu: false});
        }
    };

    signOut = () => {
        // POST with the XHR header: a plain link/img on another site must not be able to log the user out
        axios.post(window.SERVER_URL + '/sign-out', {}, {headers: {"X-Requested-With": "XMLHttpRequest"}}).finally(() => {
            window.location.href = window.SERVER_URL + '/login?error=logged_out';
        });
    };

    switchCompany = (realmId) => {
        this.setState({isSwitchingCompany: true, showAccountMenu: false});
        axios.post(window.SERVER_URL + '/switch_company', {realm_id: realmId}).then(() => {
            window.location.href = "/dashboard";
        }).catch(() => {
            this.setState({isSwitchingCompany: false});
            showGenericErrorToast();
        });
    };

    toggleSidebar = () => {
        this.setState((prevState) => {
            const collapsed = !prevState.sidebarCollapsed;
            localStorage.setItem("sidebarCollapsed", collapsed ? "true" : "false");
            return {sidebarCollapsed: collapsed};
        });
    };

    closeSidebarOnMobile = () => {
        if (window.innerWidth <= 640) {
            this.setState({sidebarCollapsed: true});
        }
    };

    componentDidMount() {
        document.addEventListener("mousedown", this.handleOutsideClick);
        // ?error= carries a code only; loginErrorFromLocation maps it to our own text (never shows the URL's text).
        const notice = loginErrorFromLocation(this.props.location);
        if (notice) {
            toast.error(notice.text);
            this.props.history.replace(this.props.location.pathname);
        }

        axios.get(window.SERVER_URL + '/get_settings').then((res) => {
            const settings = res.data.result;
            // The admin of a company still paying for a plan that's no longer offered must pick a new one: the modal
            // blocks the app on every load until they do. Members can't change the plan, so they don't get it. A frozen
            // company (failed payment) doesn't either - a plan change would charge the failing card, and it needs the
            // Usage & Plan page's "reactivate" banner to fix its payment method; once that payment goes through, it does.
            const mustChooseNewPlan = settings.is_admin === true && settings.plan_id > 1 && settings.plan_is_offered === false
                && settings.has_active_paid_subscription === true && settings.is_active !== false;
            this.setState({
                userSettings: settings,
                showLegacyPlanModal: mustChooseNewPlan,
            });
        });

        axios.get(window.SERVER_URL + '/get_user_companies').then((res) => {
            this.setState({companies: res.data.result});
        });
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleOutsideClick);
    }

  render(): React.Node {
    const isAdmin = this.state.userSettings !== null && this.state.userSettings.is_admin === true;
    const items = [...navItems, ...(isAdmin ? adminNavItems : []), settingsNavItem];
    const isUserSettingsLoading = this.state.userSettings === null;
    const companyName = this.state.userSettings !== null && this.state.userSettings.company_name
        ? this.state.userSettings.company_name : "Your Company";

    const collapsed = this.state.sidebarCollapsed;
    return (
      <div className="app-shell">
        <ToastContainer position="bottom-center" />
        {this.state.showLegacyPlanModal ?
          <LegacyPlanModal userSettings={this.state.userSettings} onSignOut={this.signOut} />
        : null}
        {this.state.isSwitchingCompany ?
          <div className="app-switching-company-overlay">
            <div className="app-switching-company-spinner" />
            <div className="app-switching-company-text">Switching company…</div>
          </div>
        : null}
        <header className="app-topbar">
          <div style={{display: "flex", alignItems: "center", gap: "16px"}}>
            <a href="/dashboard" className="app-topbar-logo"><img src={logo} alt="Scan2Pay, formerly QRify" /></a>
          </div>
          <div className="app-topbar-right">
            <div className="app-account-menu" ref={this.accountMenuRef}>
              <div className="app-company-dropdown" onClick={isUserSettingsLoading ? undefined : this.toggleAccountMenu}>
                {isUserSettingsLoading ? <span className="app-company-dropdown-skeleton" /> : <React.Fragment><span className="app-company-dropdown-name">{companyName}</span> <ChevronDownIcon size={14} /></React.Fragment>}
              </div>
              {this.state.showAccountMenu ?
                <div className="app-account-menu-dropdown">
                  {this.state.companies.length > 1 ?
                    <React.Fragment>
                      <div className="app-account-menu-section-label">Companies</div>
                      {this.state.companies.map((company) => {
                        const isActive = this.state.userSettings !== null && company.realm_id === this.state.userSettings.active_realm_id;
                        return (
                          <div key={company.realm_id}
                               className={"app-account-menu-item" + (isActive ? " app-account-menu-item-active" : "")}
                               onClick={isActive ? undefined : () => this.switchCompany(company.realm_id)}>
                            <GridIcon size={16} /> {company.company_name || "Untitled Company"}
                          </div>
                        );
                      })}
                      <div className="app-account-menu-divider" />
                    </React.Fragment>
                  : null}
                  <a className="app-account-menu-item" href={window.SERVER_URL + "/oauth/init"}>
                    <PlusCircleIcon size={16} /> Connect a new company
                  </a>
                  <a className="app-account-menu-item" href="javascript:void(0)" onClick={() => window.$crisp && window.$crisp.push(["do", "chat:open"])}>
                    <HelpCircleIcon size={16} /> Contact Support
                  </a>
                  <a className="app-account-menu-item" href="javascript:void(0)" onClick={this.signOut}>
                    <LogOutIcon size={16} /> Sign out
                  </a>
                </div>
              : null}
            </div>
          </div>
        </header>
        <button className={"app-menu-toggle" + (collapsed ? " app-menu-toggle-collapsed" : "")} onClick={this.toggleSidebar} title={collapsed ? "Expand menu" : "Collapse menu"} aria-label="Toggle menu">
          <PlayIcon size={14} style={{transform: collapsed ? "none" : "rotate(180deg)"}} />
        </button>
        <div className={"app-sidebar-backdrop" + (!collapsed ? " app-sidebar-backdrop-visible" : "")} onClick={this.closeSidebarOnMobile} />
        <aside className={"app-sidebar" + (collapsed ? " app-sidebar-collapsed" : "")}>
          <ul className="app-sidebar-nav">
            {items.map((item) => {
              const ItemIcon = item.icon;
              return (
                <li className="app-sidebar-nav-item" key={item.to} title={collapsed ? item.value : undefined} onClick={this.closeSidebarOnMobile}>
                    <NavLink to={item.to} exact
                             isActive={(match, location) => !!match || (item.alsoActiveOn || []).includes(location.pathname)}>
                        <span className="app-sidebar-nav-icon"><ItemIcon /></span>
                        {!collapsed ? <span className="app-sidebar-nav-label">{item.value}</span> : null}
                    </NavLink>
                </li>
              );
            })}
          </ul>
        </aside>
        <main className={"app-main-content" + (collapsed ? " app-main-content-expanded" : "")}>
          <div className="app-main-content-body">{this.props.children}</div>
          <AppFooter inApp />
        </main>
      </div>
    );
  }
}

export default withRouter(SiteWrapper);
